import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { LettingFeatureSelection } from '../../model/letting-feature-selection';

const LETTING_FEATURE_ID_CONTROL = 'lettingFeatureId';
const ROW_KEY_CONTROL = 'rowKey';

@Component({
    selector: 'letting-feature-control',
    template: `
    <section class="astral-section-surface astral-stack-m">
      <h5 class="astral-section-heading">{{ includedControl.caption | translate }}</h5>
      @if (includedForms) {
        @for (group of includedForms.controls; track group.controls['rowKey'].value; let index = $index) {
          <astral-layout-group [columns]="4" [tabletColumns]="2" [mobileColumns]="1" alignItems="center">
            <astral-select
              class="astral-grid-span-2"
              [control]="group.controls['lettingFeatureId']"
              [itemsSource]="getSelectionItems(group)"
            ></astral-select>
            @if (!readonly) {
              <div class="astral-form-adjacent-action astral-form-adjacent-action--end">
                <astral-button variant="secondary" size="small" type="button" icon="trash" labelKey="DELETE_BTN" (click)="removeFeature(includedForms, index)"></astral-button>
              </div>
            }
          </astral-layout-group>
        }
      }
    </section>

    <section class="astral-section-surface astral-stack-m">
      <h5 class="astral-section-heading">{{ notIncludedControl.caption | translate }}</h5>
      @if (notIncludedForms) {
        @for (group of notIncludedForms.controls; track group.controls['rowKey'].value; let index = $index) {
          <astral-layout-group [columns]="4" [tabletColumns]="2" [mobileColumns]="1" alignItems="center">
            <astral-select
              class="astral-grid-span-2"
              [control]="group.controls['lettingFeatureId']"
              [itemsSource]="getSelectionItems(group)"
            ></astral-select>
            @if (!readonly) {
              <div class="astral-form-adjacent-action astral-form-adjacent-action--end">
                <astral-button variant="secondary" size="small" type="button" icon="trash" labelKey="DELETE_BTN" (click)="removeFeature(notIncludedForms, index)"></astral-button>
              </div>
            }
          </astral-layout-group>
        }
      }
    </section>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LettingFeatureControl implements OnInit, OnDestroy {
  @Input({ required: true }) availableFeatures: LettingFeatureSelection[] = [];

  private _includedControl: FormControlEx<string[]>;
  @Input({ required: true }) set includedControl(control: AbstractControl) {
    this._includedControl = control as FormControlEx<string[]>;
  }
  get includedControl(): FormControlEx<string[]> {
    return this._includedControl;
  }

  private _notIncludedControl: FormControlEx<string[]>;
  @Input({ required: true }) set notIncludedControl(control: AbstractControl) {
    this._notIncludedControl = control as FormControlEx<string[]>;
  }
  get notIncludedControl(): FormControlEx<string[]> {
    return this._notIncludedControl;
  }

  includedForms: FormArray<FormGroup>;
  notIncludedForms: FormArray<FormGroup>;
  private readonly _selectionItemsByGroup = new Map<FormGroup, SelectionItem<string>[]>();
  private readonly _subscriptions = new Subscription();
  private _nextRowId = 0;
  private _synchronizing = false;

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _formFactory: FormControlExFactory,
    private readonly _languageService: LanguageService
  ) {}

  get readonly(): boolean {
    return this.includedControl.isReadonly || this.notIncludedControl.isReadonly;
  }

  ngOnInit(): void {
    this.includedForms = this._createFeatureForms(this.includedControl.value, this.includedControl);
    this.notIncludedForms = this._createFeatureForms(this.notIncludedControl.value, this.notIncludedControl);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  removeFeature(forms: FormArray<FormGroup>, index: number): void {
    const group = forms.at(index);
    this._selectionItemsByGroup.delete(group);
    forms.removeAt(index);
    this._syncFeatureIds();
  }

  getSelectionItems(group: FormGroup): SelectionItem<string>[] {
    const cachedItems = this._selectionItemsByGroup.get(group);
    if (cachedItems) {
      return cachedItems;
    }

    const items = this.availableFeatures.map(feature => {
      const caption = this._getFeatureCaption(feature);
      const name = feature.isActive ? caption : `${caption} (${this._languageService.instant('LETTING_FEATURE.INACTIVE')})`;
      const isDisabled = () => !feature.isActive || this._isSelectedByAnotherRow(feature.id, group);
      return {
        name,
        value: feature.id,
        get disabled() {
          return isDisabled();
        },
      } as SelectionItem<string>;
    });

    this._selectionItemsByGroup.set(group, items);
    return items;
  }

  private _createFeatureForms(featureIds: string[], targetControl: FormControlEx<string[]>): FormArray<FormGroup> {
    const forms = this._formBuilder.array<FormGroup>([]);
    (featureIds ?? []).forEach(featureId => forms.push(this._createFeatureGroup(featureId, targetControl)));

    if (!this.readonly) {
      forms.push(this._createFeatureGroup(null, targetControl));
    }

    return forms;
  }

  private _createFeatureGroup(featureId: string, targetControl: FormControlEx<string[]>): FormGroup {
    const selectedFeature = this.availableFeatures.find(feature => feature.id === featureId);
    const control = this._formFactory.createWithOptions('RENTAL_OBJECT.LETTING_FEATURES.FEATURE', featureId, {
      isReadonly: targetControl.isReadonly || (!!selectedFeature && !selectedFeature.isActive),
    });
    const group = this._formBuilder.group({
      lettingFeatureId: control,
      [ROW_KEY_CONTROL]: this._formBuilder.control(featureId || `new-${++this._nextRowId}`),
    });

    this._subscriptions.add(control.valueChanges.subscribe(() => this._syncFeatureIds()));
    return group;
  }

  private _syncFeatureIds(): void {
    if (this._synchronizing) {
      return;
    }

    this._synchronizing = true;
    this._removeEmptyRows(this.includedForms);
    this._removeEmptyRows(this.notIncludedForms);

    this.includedControl.setValue(this._getFeatureIds(this.includedForms));
    this.notIncludedControl.setValue(this._getFeatureIds(this.notIncludedForms));
    this.includedControl.markAsDirty();
    this.notIncludedControl.markAsDirty();

    if (!this.readonly) {
      this.includedForms.push(this._createFeatureGroup(null, this.includedControl));
      this.notIncludedForms.push(this._createFeatureGroup(null, this.notIncludedControl));
    }

    this._synchronizing = false;
  }

  private _removeEmptyRows(forms: FormArray<FormGroup>): void {
    for (let index = forms.length - 1; index >= 0; index--) {
      const group = forms.at(index);
      if (!group.get(LETTING_FEATURE_ID_CONTROL).value) {
        this._selectionItemsByGroup.delete(group);
        forms.removeAt(index);
      }
    }
  }

  private _getFeatureIds(forms: FormArray<FormGroup>): string[] {
    return forms.controls
      .map(group => group.get(LETTING_FEATURE_ID_CONTROL).getRawValue())
      .filter((featureId): featureId is string => !!featureId);
  }

  private _isSelectedByAnotherRow(featureId: string, group: FormGroup): boolean {
    return [...this.includedForms.controls, ...this.notIncludedForms.controls].some(
      form => form !== group && form.get(LETTING_FEATURE_ID_CONTROL).getRawValue() === featureId
    );
  }

  private _getFeatureCaption(feature: LettingFeatureSelection): string {
    return feature.phrase.find(translation => translation.cultureName === this._languageService.currentCultureLang)?.translationText
      ?? feature.phrase.find(translation => !!translation.translationText)?.translationText
      ?? '';
  }
}
